import React, { useState } from 'react';
import './index.scss';

type SceneItem = { title: string; description: string; image: string };

const frameworkImg = '/images/aip/supply-chain-planning/framework.png';
const sopImg = '/images/aip/supply-chain-planning/s-and-op.png';
const demandImg = '/images/aip/supply-chain-planning/demand-planning.png';
const supplyImg = '/images/aip/supply-chain-planning/supply-planning.png';
const inventoryImg = '/images/aip/supply-chain-planning/inventory-optimization.png';
const case1 = '/images/aip/supply-chain-planning/case-01.png';
const case2 = '/images/aip/supply-chain-planning/case-02.png';

const sopScenes: SceneItem[] = [
  {
    title: '多层级计划提报与汇总',
    description:
      '支持集团 - 事业部 - 大区自下而上提报、自上而下汇总，实现计划版本管控、自动预测与进度预警，保障数据一次录入全局应用。',
    image: sopImg
  },
  {
    title: '多约束自动供需平衡',
    description: '基于产能、物料、库存等约束自动算供需缺口，完成组织内 / 跨组织资源分配与成本寻优，输出最优供需平衡方案。',
    image: sopImg
  },
  {
    title: 'S&OP 智能决策发布',
    description:
      '提供战略仪表盘、异常聚焦看板、自动决策清单，支撑经营会议从沟通会转为数据化决策会，异常自动预警与归因。',
    image: sopImg
  }
];

const demandScenes: SceneItem[] = [
  {
    title: '产品智能分类管理',
    description: '按销量、稀疏度、缺货率、生命周期自动分层畅销品 / 长尾品 / 新品，匹配差异化预测与运营策略。',
    image: demandImg
  },
  {
    title: '多元算法智能预测',
    description: '融合时间序列、机器学习等模型，接入天气、促销、节假日等特征，适配多渠道、多 SKU、高波动场景预测。',
    image: demandImg
  },
  {
    title: '预测结果可视化分析',
    description: '全局 - 门店 - SKU 三层下钻展示预测准确率、趋势与误差，定位异常并指导补货与生产。',
    image: demandImg
  },
  {
    title: '滚动需求计划编制',
    description: '年度计划联动月 / 周 / 日滚动调整，结合供需平衡、供应与财务评估，发布可执行供应计划。',
    image: demandImg
  }
];

const supplyScenes: SceneItem[] = [
  {
    title: '主生产计划（MPS）',
    description: '对接需求与产能，编制产品 + 时间 + 数量的生产指令，支持配置、粗产能与订单优先级管理。',
    image: supplyImg
  },
  {
    title: '物料需求计划（MRP）',
    description: '精准算料、齐套性检查、缺料预警，图形化展示供需与在途，生成计划订单。',
    image: supplyImg
  },
  {
    title: '高级排产计划（APS）',
    description: '多约束最优算法自动排程，甘特图可视化，平衡交期、效率与资源利用率。',
    image: supplyImg
  },
  {
    title: '委外生产协同',
    description: '计划、进度、物料双向可视，消除信息断层，从被动催单转为主动管理。',
    image: supplyImg
  }
];

const inventoryScenes: SceneItem[] = [
  {
    title: '智能库存参数计算',
    description: 'AI 动态算安全库存、再订货点，自动更新，平衡服务水平与库存成本。',
    image: inventoryImg
  },
  {
    title: '库存可用性检查',
    description: '实时校验交期、可发货量、提前发货可行性，快速响应订单查询。',
    image: inventoryImg
  },
  {
    title: '库存健康诊断预警',
    description: '多维度周转分析、呆滞 / 缺货自动预警，输出数据化改善建议。',
    image: inventoryImg
  }
];

const aiCards = [
  {
    title: 'AI管理驾驶舱',
    description: '构建覆盖战略层、管理层与执行层的AI决策体系，形成多层级“AI管理顾问团队”协同决策机制。'
  },
  {
    title: 'AI多维预测',
    description: '基于历史销售数据及促销、节假日等多维信息进行需求预测，辅助各部门进行计划提报判断。'
  },
  {
    title: 'AI库存优化',
    description: '跨越工厂与多级仓库，计算全局成本最优的备货水位与安全库存，兼顾服务水平与库存效率。'
  }
];

function SceneTabs({ items, defaultIndex = 0 }: { items: SceneItem[]; defaultIndex?: number }) {
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const active = items[activeIndex] || items[0];
  return (
    <div className="scene-pair">
      <div className="scene-nav">
        {items.map((item, index) => (
          <article
            key={item.title}
            className={`scene-nav-item ${index === activeIndex ? 'is-active' : ''}`}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => setActiveIndex(index)}
          >
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </article>
        ))}
      </div>
      <div className="scene-viewer">
        <img src={active.image} alt={`${active.title}示意图`} loading="lazy" />
      </div>
    </div>
  );
}

export default function SupplyChainPlanning() {
  return (
    <div className="supply-chain-planning-page">
      <main>
        <section className="hero section">
          <div className="container hero-grid">
            <div className="hero-content">
              <h1>供应链计划解决方案</h1>
              <p>
                在不确定性成为新常态的背景下，推动供应链计划从“静态推演”向“动态响应”的智能中枢进化。通过构建端到端贯通的计划体系，助力大型企业打破信息孤岛，实现精益运营与业务持续增长。
              </p>
            </div>
            <aside className="hero-card" aria-label="方案价值摘要">
              <div className="hero-kpi">
                <div>
                  <b>1个</b>
                  <span>核心体系</span>
                </div>
                <div>
                  <b>3 大</b>
                  <span>计划建设目标</span>
                </div>
                <div>
                  <b>AI 驱动</b>
                  <span>预测到执行智能闭环</span>
                </div>
                <div>
                  <b>端到端</b>
                  <span>打通需求生产库存交付</span>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="section section-alt" id="framework">
          <div className="container">
            <div className="section-head">
              <h2>供应链计划建设框架</h2>
              <p>
                通过构建“1个核心体系、15个核心能力中心、1个智能数字底座”三层架构，
                <br />
                打造从战略到执行、端到端贯通的智慧供应链计划中枢，实现企业供应链计划3大建设目标。
              </p>
            </div>
            <div className="fw-stack">
              <div className="fw-overview" aria-label="建设框架总览图">
                <img src={frameworkImg} alt="企业智慧供应链计划平台建设框架图" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        <section className="section" id="sop">
          <div className="container">
            <div className="section-head">
              <h2>销售与运营计划（S&amp;OP）</h2>
              <p>数据驱动采销财共识，打通多层级协同与供需决策</p>
            </div>
            <div className="scene-showcase">
              <SceneTabs items={sopScenes} />
            </div>
          </div>
        </section>

        <section className="section section-alt" id="demand">
          <div className="container">
            <div className="section-head">
              <h2>需求计划</h2>
              <p>智能预测 + 滚动计划，精准匹配市场与战略目标</p>
            </div>
            <div className="scene-showcase">
              <SceneTabs items={demandScenes} />
            </div>
          </div>
        </section>

        <section className="section" id="supply">
          <div className="container">
            <div className="section-head">
              <h2>供应计划</h2>
              <p>全链路计划闭环，从需求落地到高效生产</p>
            </div>
            <div className="scene-showcase">
              <SceneTabs items={supplyScenes} />
            </div>
          </div>
        </section>

        <section className="section section-alt" id="inventory">
          <div className="container">
            <div className="section-head">
              <h2>库存优化</h2>
              <p>智能库存管控，降本增效保障交付</p>
            </div>
            <div className="scene-showcase">
              <SceneTabs items={inventoryScenes} />
            </div>
          </div>
        </section>

        <section className="section" id="ai">
          <div className="container">
            <div className="section-head">
              <h2>AI Agent驱动</h2>
              <p>以AI驱动供应链计划全链路，从预测到执行实现智能决策闭环。</p>
            </div>
            <div className="ai-layout">
              <div className="ai-cards">
                {aiCards.map((card) => (
                  <article key={card.title} className="adv-card">
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section section-alt" id="cases">
          <div className="container">
            <div className="section-head">
              <h2>实践案例</h2>
              <p>已在多个企业进行落地，并支撑企业业务发展。</p>
            </div>
            <div className="case-grid case-grid--two">
              <a className="case-link" href="https://terminus.io/case-detail/2009001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <div className="case-media-placeholder">
                    <img src={case1} alt="三力士案例配图" />
                  </div>
                  <h3>三力士｜中国橡胶V带制造企业龙头</h3>
                  <p className="case-one-line">以“生产制造”为核心，助力企业供应链整体数字化升级。</p>
                </article>
              </a>
              <a className="case-link" href="https://terminus.io/case-detail/12080" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <div className="case-media-placeholder">
                    <img src={case2} alt="源氏木语案例配图" />
                  </div>
                  <h3>源氏木语｜连续10年稳居天猫纯实木家具类目销量TOP1</h3>
                  <p className="case-one-line">全量业务数字化蜕变，打造家居行业数字化样板间。</p>
                </article>
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

